import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import InsurancePatientInfoService from "../patientInfo/InsurancePatientInfoService";
import apiBaseUrl from "../../utils/config";
import "./searchPopupDesign.css";
import "./RegisterInsurancePatient.css";

const DEFAULT_DOCTOR_ID = 15;
const DEFAULT_DOCTOR_NAME = "Default / Not Sure";
const DEFAULT_YEARLY_VISITS = 365;

const RegisterInsurancePatient = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [patientId, setPatientId] = useState("");
  const [patientName, setPatientName] = useState("");

  const [insId, setInsId] = useState("");
  const [insuranceName, setInsuranceName] = useState("");

  const [officeId, setOfficeId] = useState("");
  const [officeName, setOfficeName] = useState("");

  const [doctorId, setDoctorId] = useState(DEFAULT_DOCTOR_ID);
  const [doctorName, setDoctorName] = useState(DEFAULT_DOCTOR_NAME);

  const [subscriber, setSubscriber] = useState("");
  const [activeOptions, setActiveOptions] = useState([]);
  const [coPay, setCoPay] = useState("");
  const [notes, setNotes] = useState("");

  const [yearlyVisitTotal, setYearlyVisitTotal] = useState(DEFAULT_YEARLY_VISITS);
  const [yearlyVisitRemaining, setYearlyVisitRemaining] = useState(DEFAULT_YEARLY_VISITS);
  const [remainingManuallyChanged, setRemainingManuallyChanged] = useState(false);

  const [lastSavedRecord, setLastSavedRecord] = useState(null);

  const insurancePatientService = new InsurancePatientInfoService();

  const getPreselectedPatientFromUrl = () => {
    const params = new URLSearchParams(location.search);

    const urlPatientId = params.get("patientId") || "";
    const urlPatientName = params.get("patientName") || "";

    return {
      urlPatientId,
      urlPatientName,
    };
  };

  const extractInsurancePatientId = (response) => {
    return (
      response?.insptId ||
      response?.inspt_id ||
      response?.insurancePatientId ||
      response?.insurancePatientInfoId ||
      response?.insurancePatientInfoMainId ||
      response?.id ||
      ""
    );
  };

  useEffect(() => {
    const { urlPatientId, urlPatientName } = getPreselectedPatientFromUrl();

    if (urlPatientId) {
      setPatientId(urlPatientId);
      setPatientName(urlPatientName || `Patient #${urlPatientId}`);
    }
  }, [location.search]);

  useEffect(() => {
    const handleMessage = (event) => {
      console.log("📥 Message received in RegisterInsurancePatient:", event.data);

      const { inputTarget, value, displayName } = event.data || {};

      if (inputTarget === "patientId") {
        console.log("✅ Setting patientId:", value, "→", displayName);
        setPatientId(value);
        setPatientName(displayName || "");
        setLastSavedRecord(null);
      }

      if (inputTarget === "insId") {
        console.log("✅ Setting insId:", value, "→", displayName);
        setInsId(value);
        setInsuranceName(displayName || "");
      }

      if (inputTarget === "officeId") {
        console.log("✅ Setting officeId:", value, "→", displayName);
        setOfficeId(value);
        setOfficeName(displayName || "");
      }

      if (inputTarget === "doctorId") {
        console.log("✅ Setting doctorId:", value, "→", displayName);
        setDoctorId(value);
        setDoctorName(displayName || `Doctor #${value}`);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => window.removeEventListener("message", handleMessage);
  }, []);

  const openSearchPopup = (target) => {
    let urlParam = "";
    let typeParam = "";
    let inputTargetParam = "";

    if (target === "patient") {
      urlParam = `${apiBaseUrl}/patientinfo/search/all`;
      typeParam = "Patient";
      inputTargetParam = "patientId";
    }

    if (target === "insurance") {
      urlParam = `${apiBaseUrl}/insurance/search`;
      typeParam = "Insurance";
      inputTargetParam = "insId";
    }

    if (target === "doctor") {
      urlParam = `${apiBaseUrl}/doctorinfo/search/all`;
      typeParam = "Doctor";
      inputTargetParam = "doctorId";
    }

    const searchUrl = `/search-popup?type=${typeParam}&inputTarget=${inputTargetParam}&url=${encodeURIComponent(
      urlParam
    )}`;

    console.log("📤 Opening popup:", searchUrl);

    window.open(searchUrl, "popup", "width=700,height=700");
  };

  const openOfficeSearch = () => {
    window.open("/office-search", "popup", "width=700,height=700");
  };

  const useDefaultDoctor = () => {
    setDoctorId(DEFAULT_DOCTOR_ID);
    setDoctorName(DEFAULT_DOCTOR_NAME);
  };

  const handleCheckboxChange = (option) => {
    setActiveOptions((prev) =>
      prev.includes(option)
        ? prev.filter((item) => item !== option)
        : [...prev, option]
    );
  };

  const handleYearlyTotalChange = (value) => {
    const parsed = parseInt(value || "0", 10);
    const safeValue =
      Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_YEARLY_VISITS;

    setYearlyVisitTotal(safeValue);

    if (!remainingManuallyChanged) {
      setYearlyVisitRemaining(safeValue);
    }
  };

  const handleYearlyRemainingChange = (value) => {
    const parsed = parseInt(value || "0", 10);
    const safeValue = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;

    setRemainingManuallyChanged(true);
    setYearlyVisitRemaining(safeValue);
  };

  const resetFormAfterSubmit = () => {
    const { urlPatientId, urlPatientName } = getPreselectedPatientFromUrl();

    setPatientId(urlPatientId || "");
    setPatientName(
      urlPatientId ? urlPatientName || `Patient #${urlPatientId}` : ""
    );

    setInsId("");
    setInsuranceName("");

    setOfficeId("");
    setOfficeName("");

    setDoctorId(DEFAULT_DOCTOR_ID);
    setDoctorName(DEFAULT_DOCTOR_NAME);

    setSubscriber("");
    setActiveOptions([]);
    setCoPay("");
    setNotes("");

    setYearlyVisitTotal(DEFAULT_YEARLY_VISITS);
    setYearlyVisitRemaining(DEFAULT_YEARLY_VISITS);
    setRemainingManuallyChanged(false);
  };

  const goToAuthorization = () => {
    const savedPatientId = lastSavedRecord?.patientId || patientId;

    const savedPatientName =
      lastSavedRecord?.patientName ||
      patientName ||
      `Patient #${savedPatientId}`;

    const savedInsptId = lastSavedRecord?.insptId || "";

    if (!savedPatientId) {
      alert("No patient ID found to send to authorization.");
      return;
    }

    if (!savedInsptId) {
      alert("Insurance record was saved, but no insurance patient ID was returned.");
      return;
    }

    const params = new URLSearchParams({
      patientId: String(savedPatientId),
      patientName: savedPatientName,
      insptId: String(savedInsptId),
    });

    navigate(`/register/authorization?${params.toString()}`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("📝 Form values before submission:", {
      patientId,
      insId,
      officeId,
      doctorId,
      subscriber,
      activeOptions,
      coPay,
      notes,
      yearlyVisitTotal,
      yearlyVisitRemaining,
    });

    if (!patientId || !insId) {
      alert("❌ Please select a patient and insurance.");
      return;
    }

    if (!officeId) {
      alert("❌ Please select a default office for this patient insurance.");
      return;
    }

    if (!doctorId) {
      alert("❌ Please select a doctor or use Default / Not Sure.");
      return;
    }

    if (!yearlyVisitTotal || yearlyVisitTotal <= 0) {
      alert("❌ Yearly visit total must be greater than 0.");
      return;
    }

    if (yearlyVisitRemaining < 0) {
      alert("❌ Yearly visit remaining cannot be negative.");
      return;
    }

    if (
      yearlyVisitTotal !== DEFAULT_YEARLY_VISITS &&
      yearlyVisitRemaining > yearlyVisitTotal
    ) {
      alert("❌ Yearly visits remaining cannot be greater than yearly visit total.");
      return;
    }

    const currentYear = new Date().getFullYear();

    const data = {
      patientId: parseInt(patientId, 10),
      insId: parseInt(insId, 10),
      officeId: parseInt(officeId, 10),
      doctorId: parseInt(doctorId, 10),
      subscriber: subscriber.trim(),
      active: activeOptions.join(","),
      coPay: String(coPay || "0").trim(),
      notes: notes.trim(),

      yearlyVisitTotal: parseInt(yearlyVisitTotal, 10),
      yearlyVisitRemaining: parseInt(yearlyVisitRemaining, 10),
      yearlyVisitResetYear: currentYear,
    };

    try {
      const response = await insurancePatientService.saveOrUpdate(data);

      const savedInsptId = extractInsurancePatientId(response);

      setLastSavedRecord({
        response,
        patientId,
        patientName: patientName || `Patient #${patientId}`,
        insptId: savedInsptId,
      });

      alert("✅ Registration Successful: " + JSON.stringify(response));

      resetFormAfterSubmit();
    } catch (err) {
      console.error("❌ Submission failed:", err);
      alert("❌ Registration Failed: " + err.message);
    }
  };

  const availableOptions = ["visit", "double", "acupuncture"];
  const yearlyLimitIsUnlimited =
    Number(yearlyVisitTotal) === DEFAULT_YEARLY_VISITS;

  return (
    <div className="register-insurance-page">
      <form className="register-insurance-form" onSubmit={handleSubmit}>
        <div className="register-insurance-header">
          <div>
            <h2>Register Patient Insurance</h2>
            <p>
              Select the patient, insurance, default office, and default doctor,
              then enter coverage details.
            </p>
          </div>
        </div>

        {lastSavedRecord && (
          <div
            style={{
              padding: "12px",
              marginBottom: "1rem",
              borderRadius: "8px",
              background: "#e7f8ec",
              color: "#155724",
              border: "1px solid #b9e8c4",
            }}
          >
            <h3 style={{ marginTop: 0 }}>Patient Insurance Registered</h3>

            <p>
              <strong>Patient ID:</strong> {lastSavedRecord.patientId}
            </p>

            <p>
              <strong>Patient:</strong> {lastSavedRecord.patientName}
            </p>

            <p>
              <strong>Insurance Patient ID:</strong>{" "}
              {lastSavedRecord.insptId || "Saved, but ID was not returned"}
            </p>

            <button
              type="button"
              onClick={goToAuthorization}
              disabled={!lastSavedRecord.insptId}
              style={{
                marginTop: "12px",
                width: "100%",
                padding: "12px",
                borderRadius: "8px",
                border: "none",
                cursor: lastSavedRecord.insptId ? "pointer" : "not-allowed",
                fontWeight: "bold",
                opacity: lastSavedRecord.insptId ? 1 : 0.6,
              }}
            >
              Register Authorization for This Patient
            </button>
          </div>
        )}

        <div className="register-insurance-grid">
          <section className="register-insurance-card">
            <h3>Patient & Insurance</h3>

            <label>Patient</label>
            <button type="button" onClick={() => openSearchPopup("patient")}>
              Search Patient
            </button>

            <input
              type="text"
              value={patientName}
              readOnly
              placeholder="Selected Patient"
            />

            <div className="register-insurance-id-text">
              Patient ID: {patientId || "N/A"}
            </div>

            <label>Insurance</label>
            <button type="button" onClick={() => openSearchPopup("insurance")}>
              Search Insurance
            </button>

            <input
              type="text"
              value={insuranceName}
              readOnly
              placeholder="Selected Insurance"
            />

            <div className="register-insurance-id-text">
              Insurance ID: {insId || "N/A"}
            </div>
          </section>

          <section className="register-insurance-card">
            <h3>Defaults</h3>

            <label>Default Office</label>
            <button type="button" onClick={openOfficeSearch}>
              Search Default Office
            </button>

            <input
              type="text"
              value={officeName}
              readOnly
              placeholder="Selected Default Office"
            />

            <div className="register-insurance-id-text">
              Office ID: {officeId || "N/A"}
            </div>

            <label>Default Doctor</label>
            <div className="register-insurance-button-row">
              <button type="button" onClick={() => openSearchPopup("doctor")}>
                Search Default Doctor
              </button>

              <button type="button" onClick={useDefaultDoctor}>
                Default / Not Sure
              </button>
            </div>

            <input
              type="text"
              value={doctorName}
              readOnly
              placeholder="Selected Default Doctor"
            />

            <div className="register-insurance-id-text">
              Doctor ID: {doctorId || "N/A"}
            </div>
          </section>

          <section className="register-insurance-card">
            <h3>Coverage Details</h3>

            <label>Subscriber ID</label>
            <input
              type="text"
              placeholder="Enter Subscriber ID"
              value={subscriber}
              onChange={(e) => setSubscriber(e.target.value)}
            />

            <label>Active Status / Visit Types</label>
            <div className="register-insurance-checkbox-grid">
              {availableOptions.map((option) => (
                <label key={option} className="register-insurance-checkbox">
                  <input
                    type="checkbox"
                    value={option}
                    checked={activeOptions.includes(option)}
                    onChange={() => handleCheckboxChange(option)}
                  />
                  <span>{option}</span>
                </label>
              ))}
            </div>
          </section>

          <section className="register-insurance-card">
            <h3>Yearly Visit Limit</h3>

            <p className="register-insurance-id-text">
              Default is 365. Keep 365 for unlimited / not counted yearly
              visits. Change it only when the insurance has a yearly visit cap.
            </p>

            <label>Total Yearly Visits</label>
            <input
              type="number"
              min="1"
              value={yearlyVisitTotal}
              onChange={(e) => handleYearlyTotalChange(e.target.value)}
            />

            <label>Yearly Visits Remaining</label>
            <input
              type="number"
              min="0"
              value={yearlyVisitRemaining}
              onChange={(e) => handleYearlyRemainingChange(e.target.value)}
            />

            <div className="register-insurance-id-text">
              {yearlyLimitIsUnlimited
                ? "Status: Unlimited / not counted down"
                : `Status: Counts down yearly. ${yearlyVisitRemaining} of ${yearlyVisitTotal} remaining.`}
            </div>
          </section>

          <section className="register-insurance-card">
            <h3>Payment & Notes</h3>

            <label>Co-Pay</label>
            <input
              type="number"
              placeholder="Enter Co-Pay"
              value={coPay}
              onChange={(e) => setCoPay(e.target.value)}
            />

            <label>Notes</label>
            <textarea
              placeholder="Enter Notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />

            <div className="register-insurance-actions">
              <button type="submit">Register Insurance Patient</button>
            </div>
          </section>
        </div>
      </form>
    </div>
  );
};

export default RegisterInsurancePatient;